import { serverRefusalMessage } from "@/lib/serverError";

/**
 * An upload refusal, carrying whether trying again is safe.
 *
 * `/tables/ipc/create` mints a table per call and takes no idempotency key, so
 * a retry after the write began makes a second copy and bills it again. Only
 * the worker knows whether a byte left, and only the server says whether it
 * refused before touching storage — classifying that from the message text
 * downstream is what made a 504 look like a transient network blip.
 */
export class ImportUploadError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    /** The message is the server's own sentence for a refusal it issued
     *  instead of writing, so it is shown as it is. */
    readonly refused = false,
    /** The server's refusal code, when it sent one. */
    readonly code?: string,
  ) {
    super(message);
    this.name = "ImportUploadError";
  }
}

/** Upload-door refusals of the bytes themselves: nothing reached storage, so
 *  trying again is safe and "check whether the table arrived" is wrong. */
const NOTHING_LANDED_CODES = new Set([
  "upload_expired",
  "size_mismatch",
  "upload_not_landed",
  "too_many_concurrent_uploads",
  "source_unreadable",
]);

/** A worded 4xx is a gate's refusal (plan, storage, permission): final, and
 *  shown as the server said it. Every other refusal keeps its status in the
 *  text for the row to translate, because the queue may retry it first.
 *  `beforeAnyByte`: the refusal answered `start`, so nothing was sent and any
 *  status — a 404 from a pod still rolling out included — is safe to retry,
 *  except a gate's own sentence, which no retry changes. */
export function uploadRefusal(
  status: number,
  body: string,
  failed?: string,
  beforeAnyByte = false,
): ImportUploadError {
  const said = serverRefusalMessage(body);
  const code = refusalCode(body);
  const nothingLanded = NOTHING_LANDED_CODES.has(code ?? "");
  const gate =
    status < 500 && said !== null && status !== 429 && !nothingLanded;
  return new ImportUploadError(
    gate
      ? said
      : (failed ?? `Import failed with status ${status}: ${said ?? body}`),
    !gate &&
      (beforeAnyByte || refusedBeforeAnyWrite(status, body) || nothingLanded),
    gate,
    code,
  );
}

function refusalCode(body: string): string | undefined {
  try {
    const code = (JSON.parse(body) as { error?: unknown }).error;
    return typeof code === "string" ? code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A refusal issued before the handler touches storage: the pod's upload slot
 * (`upload_capacity_exhausted`) and rate limiting. Every other status can
 * arrive after a commit — a 504 in particular lands while the Lance write from
 * the last byte is still running, on the local proxy and through ds-proxy.
 */
function refusedBeforeAnyWrite(status: number, body: string): boolean {
  if (status === 429) return true;
  return status === 503 && body.includes("upload_capacity_exhausted");
}
